import {useQueryParams} from 'presentation/pages';
import {usePostOdontoProvidersMutation} from '../../mutations/usePostOdontoProvidersMutation';
import {usePostProvidersDentalGuideMutation} from '../../mutations/usePostProvidersDentalGuideMutation';
import {useUnitsFetchStateStore} from 'presentation/modules/booking/shared/store/useUnitsFetchStateStore';
import {
	DisabilityEnum,
	FeaturesEnum,
	OperationStateTypesEnum,
	QueryParamsEnum,
} from 'presentation/constants';
import {useEffect, useRef, useState} from 'react';
import {
	KM_TO_REFETCH,
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
	MIN_DISTANCE_IN_KM,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {NetworkTypesEnum} from 'domain/usecases';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import axios, {AxiosError} from 'axios';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useFilteredNetworkList} from 'presentation/modules/booking/shared/hooks/useFilteredNetworkList';
import {Unit} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function useOdontoUnits() {
	const bookingData = useBookingStore(state => state.bookingData);
	const operationState = useUnitsFetchStateStore(state => state.unitListFetchState);
	const setOperationState = useUnitsFetchStateStore(state => state.setUnitListFetchState);
	const unitResponseState = useUnitsListStore(state => state.unitsList);
	const setUnitResponseState = useUnitsListStore(state => state.setUnitsList);
	const [notFoundErrorState, setNotFoundErrorState] = useState(false);
	const {
		SEARCH_PARAMS: {query, localityDistance, disability, distanceOrigin},
		setSearchParams,
	} = useQueryParams();
	const abortControllerRef = useRef<AbortController | null>(null);
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const isManualDistanceSearch = distanceOrigin === MANUAL_DISTANCE_ORIGIN;

	const {mutateAsync: getOdontoUnitsMutation} = usePostOdontoProvidersMutation();
	const {mutateAsync: getOdontoAccreditedUnitsMutation} = usePostProvidersDentalGuideMutation();

	const filteredNetworkList = useFilteredNetworkList(query) as Unit<FlowEnum.ODONTO>;
	const filteredListIsEmpty = filteredNetworkList?.units?.unitsByLocality?.length === 0;

	const showLoadingState =
		operationState === OperationStateTypesEnum.SUBMITTING ||
		operationState === OperationStateTypesEnum.PENDING;
	const showErrorState = operationState === OperationStateTypesEnum.ERROR && !notFoundErrorState;
	const notFoundUnits =
		unitResponseState?.units?.unitsByLocality?.length === 0 || notFoundErrorState;
	const hasAnyUnit = Boolean(unitResponseState?.units?.unitsByLocality?.length);

	const showNotFoundState = isUnitsNetworksEnabled
		? notFoundErrorState || (Boolean(query) && filteredListIsEmpty && hasAnyUnit)
		: notFoundUnits || filteredListIsEmpty;

	const showSelfNetwork = unitResponseState?.networkType?.name === NetworkTypesEnum.SELF;
	const showSupportAccreditedNetwork =
		unitResponseState?.networkType?.name === NetworkTypesEnum.SUPPORT;

	const handleFetchUnits = async () => {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		setNotFoundErrorState(false);
		abortControllerRef.current = new AbortController();

		try {
			const {units} = await getOdontoUnitsMutation({
				signal: abortControllerRef.current.signal,
			});

			const isUsingGeoAndHasNoDistrictsFilter =
				!bookingData.isManualLocation && !bookingData.hasGeoDistrictsFilter;

			const shouldRefetchUnits =
				units?.length === 0 &&
				(!isUnitsNetworksEnabled || !isManualDistanceSearch) &&
				isUsingGeoAndHasNoDistrictsFilter &&
				(!localityDistance || localityDistance === MIN_DISTANCE_IN_KM);

			if (shouldRefetchUnits) {
				setRefetchSearchParams();

				return;
			}

			if (units?.length === 0 && disability === DisabilityEnum.ALL) {
				const {units: accreditedUnits} = await getOdontoAccreditedUnitsMutation({
					signal: abortControllerRef.current?.signal,
				});

				if (accreditedUnits?.length === 0) {
					setUnitResponseState({
						networkType: {code: '', name: NetworkTypesEnum.SELF},
						units: {
							unitsByLocality: [],
							unitsByDistance: [],
							unitsSupportAccredited: [],
						},
					});

					setOperationState(OperationStateTypesEnum.DONE);

					return;
				}

				setUnitResponseState({
					networkType: {
						code: '',
						name: NetworkTypesEnum.SUPPORT,
					},
					units: {
						unitsByLocality: accreditedUnits,
						unitsByDistance: [],
						unitsSupportAccredited: [],
					},
				});

				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			setUnitResponseState({
				networkType: {
					code: '',
					name: NetworkTypesEnum.SELF,
				},
				units: {
					unitsByLocality: units,
					unitsByDistance: [],
					unitsSupportAccredited: [],
				},
			});

			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (axios.isAxiosError(error) && error.code === AxiosError.ERR_CANCELED) {
				setOperationState(OperationStateTypesEnum.SUBMITTING);
				return;
			}

			if (isUnprocessableError(error)) {
				setNotFoundErrorState(true);
			}
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const setRefetchSearchParams = () => {
		const params = new URLSearchParams(window.location.search);
		params.set(
			QueryParamsEnum.LOCALITY_DISTANCE,
			isUnitsNetworksEnabled ? MAX_DISTANCE_FILTER_UNITS_ODONTO.toString() : KM_TO_REFETCH,
		);
		params.delete(QueryParamsEnum.DISTANCE_ORIGIN);
		params.delete(QueryParamsEnum.SEARCH_QUERY);
		setSearchParams(params);
	};

	useEffect(() => {
		handleFetchUnits();
	}, [disability, localityDistance, bookingData]);

	useEffect(
		() => () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		},
		[],
	);

	return {
		showLoadingState,
		showErrorState,
		showNotFoundState,
		query,
		filteredNetworkList,
		handleRefetchUnits: handleFetchUnits,
		showSelfNetwork,
		showSupportAccreditedNetwork,
	};
}
